/**
 * LaTeX Rendering Utility for Mathematical Formulas
 *
 * Uses KaTeX to render LaTeX math expressions in AI-generated content
 * Supports both inline ($...$) and display ($$...$$) math
 *
 * Example LaTeX formats:
 * - Inline: $x^2 + y^2 = r^2$
 * - Display: $$\frac{-b \pm \sqrt{b^2-4ac}}{2a}$$
 * - Complex: $$\int_{0}^{\infty} e^{-x^2} dx = \frac{\sqrt{\pi}}{2}$$
 */

import katex from "katex";
import "katex/dist/katex.min.css";
import { logDebug, logError, logWarn } from "./secureLogger";
import { sanitizeHtml } from "./xssSanitizer";

/**
 * Convert basic markdown formatting to HTML
 * Handles bold (**text**), italic (*text*), and code (`text`)
 */
function convertMarkdownToHtml(text: string): string {
  if (!text) return "";

  let converted = text;

  // Bold: **text** or __text__ → <strong>text</strong>
  converted = converted.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  converted = converted.replace(/__(.+?)__/g, "<strong>$1</strong>");

  // Italic: *text* or _text_ → <em>text</em> (but not if it's part of **)
  // Use negative lookbehind/lookahead to avoid matching ** patterns
  converted = converted.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, "<em>$1</em>");
  converted = converted.replace(/(?<!_)_(?!_)(.+?)(?<!_)_(?!_)/g, "<em>$1</em>");

  // Inline code: `text` → <code>text</code>
  converted = converted.replace(/`(.+?)`/g, "<code>$1</code>");

  return converted;
}

/**
 * Render LaTeX expressions in HTML string
 * Detects and renders both inline ($...$) and display ($$...$$) math
 * Also converts markdown formatting (bold, italic) to HTML
 *
 * @param text - HTML string containing LaTeX expressions and markdown
 * @returns HTML string with rendered LaTeX and converted markdown
 */
export function renderLatex(text: string): string {
  if (!text) return "";

  try {
    let rendered = text;

    // First, convert markdown formatting to HTML
    rendered = convertMarkdownToHtml(rendered);

    // Then, render display math ($$...$$) - must be done before inline math
    // to avoid conflicts with nested $ symbols
    rendered = rendered.replace(/\$\$([\s\S]+?)\$\$/g, (match, latex) => {
      try {
        const cleaned = latex.trim();
        const html = katex.renderToString(cleaned, {
          displayMode: true,
          throwOnError: false,
          output: "html",
          strict: false,
          trust: false, // Security: Don't allow \href, \includegraphics, etc.
        });

        logDebug("Rendered display LaTeX", {
          original: cleaned.substring(0, 50),
          success: true,
        });

        return `<span class="katex-display-wrapper">${html}</span>`;
      } catch (error) {
        logWarn("Failed to render display LaTeX, showing raw", {
          latex: latex.substring(0, 50),
          error: error instanceof Error ? error.message : String(error),
        });
        // Fallback: show raw LaTeX in a styled box
        return `<span class="latex-error" title="LaTeX rendering failed">$$${sanitizeHtml(
          latex
        )}$$</span>`;
      }
    });

    // Then, render inline math ($...$)
    rendered = rendered.replace(/\$([^$]+?)\$/g, (match, latex) => {
      try {
        const cleaned = latex.trim();
        const html = katex.renderToString(cleaned, {
          displayMode: false,
          throwOnError: false,
          output: "html",
          strict: false,
          trust: false, // Security: Don't allow dangerous commands
        });

        logDebug("Rendered inline LaTeX", {
          original: cleaned.substring(0, 30),
          success: true,
        });

        return `<span class="katex-inline-wrapper">${html}</span>`;
      } catch (error) {
        logWarn("Failed to render inline LaTeX, showing raw", {
          latex: latex.substring(0, 30),
          error: error instanceof Error ? error.message : String(error),
        });
        // Fallback: show raw LaTeX in italics
        return `<span class="latex-error" title="LaTeX rendering failed">$${sanitizeHtml(
          latex
        )}$</span>`;
      }
    });

    return rendered;
  } catch (error) {
    logError("LaTeX rendering error", error);
    return text; // Return original text if rendering fails completely
  }
}

/**
 * Check if text contains LaTeX expressions
 * Useful for conditional rendering logic
 */
export function containsLatex(text: string): boolean {
  if (!text) return false;

  // Check for both inline ($...$) and display ($$...$$) math
  const hasDisplayMath = /\$\$[\s\S]+?\$\$/.test(text);
  const hasInlineMath = /\$[^$]+?\$/.test(text);

  return hasDisplayMath || hasInlineMath;
}

/**
 * Extract all LaTeX expressions from text
 * Returns array of LaTeX strings without delimiters
 */
export function extractLatexExpressions(text: string): string[] {
  if (!text) return [];

  const expressions: string[] = [];

  // Extract display math
  const displayMatches = text.match(/\$\$([\s\S]+?)\$\$/g);
  if (displayMatches) {
    expressions.push(...displayMatches.map((m) => m.replace(/^\$\$|\$\$/g, "").trim()));
  }

  // Extract inline math
  const inlineMatches = text.match(/\$([^$]+?)\$/g);
  if (inlineMatches) {
    expressions.push(...inlineMatches.map((m) => m.replace(/^\$|\$/g, "").trim()));
  }

  logDebug("Extracted LaTeX expressions", {
    totalCount: expressions.length,
    displayCount: displayMatches?.length || 0,
    inlineCount: inlineMatches?.length || 0,
  });

  return expressions;
}

/**
 * Validate LaTeX syntax without rendering
 * Returns true if LaTeX is valid, false otherwise
 */
export function validateLatex(latex: string): boolean {
  try {
    katex.renderToString(latex, {
      throwOnError: true,
      output: "html",
    });
    return true;
  } catch (error) {
    logDebug("LaTeX validation failed", {
      latex: latex.substring(0, 50),
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
}
